/**
 * Mock API implementations
 * 
 * These functions provide local/mock implementations when backend is disabled
 */

import { ProductsArray } from '../../types';
import { SavedShoppingList } from '../../redux/shoppingListSlice';
import {
    ProductsResponse,
    ProductResponse,
    GetProductsParams,
    StoreLayoutResponse,
    StoreLayoutGraphPayload,
    StoresResponse,
    StoreResponse,
    ShoppingListResponse,
    ShoppingListsResponse,
    CreateShoppingListRequest,
    UpdateShoppingListRequest,
    OptimizeRouteRequest,
    OptimizeRouteResponse,
    Persona,
} from '../types';

/** Minimal store_layout_graph for mock when backend is disabled */
const mockStoreLayoutGraph: StoreLayoutGraphPayload = {
    nodes: {
        '0': { node_id: 0, coords: [100, 100], products: [] },
        '1': { node_id: 1, coords: [200, 100], products: ['123'] },
        '2': { node_id: 2, coords: [300, 100], products: [] },
    },
    edges: [
        { source: 0, target: 1, length: 100 },
        { source: 1, target: 2, length: 100 },
    ],
};

// Mock delay to simulate network latency
const mockDelay = (ms: number = 500) => new Promise(resolve => setTimeout(resolve, ms));

// Load products from local JSON (existing implementation)
const loadLocalProducts = async (): Promise<ProductsArray> => {
    try {
        const productsModule = await import('../../assets/data/products.json');
        const { transformStoreDataArray } = await import('../../utils/transformStoreData');
        return transformStoreDataArray(productsModule.default);
    } catch (error) {
        console.warn('Failed to load local products:', error);
        const { loadProductsSafely } = await import('../../utils/jsonLoader');
        return await loadProductsSafely();
    }
};

// Mock Products API
export const mockProductsApi = {
    getProducts: async (params?: GetProductsParams): Promise<ProductsResponse> => {
        await mockDelay();
        const allProducts = await loadLocalProducts();
        
        let filtered = [...allProducts];
        
        // Apply filters
        if (params?.category) {
            filtered = filtered.filter(p => p.category === params.category);
        }
        
        if (params?.search) {
            const searchTerm = params.search.toLowerCase();
            filtered = filtered.filter(p =>
                p.title.toLowerCase().includes(searchTerm) ||
                p.brand.toLowerCase().includes(searchTerm) ||
                p.shortDescription.toLowerCase().includes(searchTerm)
            );
        }
        
        // Pagination
        const page = params?.page || 1;
        const limit = params?.limit || 20;
        const start = (page - 1) * limit;
        const end = start + limit;
        
        return {
            products: filtered.slice(start, end),
            total: filtered.length,
            page,
            limit,
            totalPages: Math.ceil(filtered.length / limit),
        };
    },

    getProductById: async (productId: string): Promise<ProductResponse> => {
        await mockDelay();
        const allProducts = await loadLocalProducts();
        const product = allProducts.find(p => p.unique_id === productId);
        
        if (!product) {
            throw new Error(`Product with ID ${productId} not found`);
        }
        
        return { product };
    },

    searchProducts: async (query: string, params?: Omit<GetProductsParams, 'search'>): Promise<ProductsResponse> => {
        return mockProductsApi.getProducts({ ...params, search: query });
    },

    getProductsByCategory: async (category: string, params?: Omit<GetProductsParams, 'category'>): Promise<ProductsResponse> => {
        return mockProductsApi.getProducts({ ...params, category });
    },

    getCategories: async (): Promise<string[]> => {
        await mockDelay();
        const allProducts = await loadLocalProducts();
        const categories = new Set(
            allProducts
                .map(p => p.category)
                .filter(c => c && c.trim() !== '')
        );
        return Array.from(categories).sort();
    },
};

// Mock Store API
export const mockStoreApi = {
    getStoreLayout: async (storeId: string): Promise<StoreLayoutResponse> => {
        await mockDelay();
        // Return mock layout - in real app, this would come from local data
        return {
            storeId,
            nodes: [],
            locationOverrides: {},
        };
    },

    getStores: async (): Promise<StoresResponse> => {
        await mockDelay();
        // Return mock stores with new structure
        return {
            stores: [
                {
                    id: 0,
                    name: 'Continente Vasco da Gama',
                    store_layout_graph: mockStoreLayoutGraph,
                    created_at: new Date().toISOString(),
                    updated_at: new Date().toISOString(),
                },
            ],
            total: 1,
        };
    },

    getStoreById: async (storeId: string | number): Promise<StoreResponse> => {
        await mockDelay();
        const stores = await mockStoreApi.getStores();
        const storeIdNum = typeof storeId === 'string' ? parseInt(storeId, 10) : storeId;
        const store = Number.isFinite(storeIdNum)
            ? stores.stores.find(s => s.id === storeIdNum)
            : stores.stores[0]; // fallback for non-numeric id (e.g. "continente-1") when backend disabled
        if (!store) {
            throw new Error(`Store with ID ${storeId} not found`);
        }
        return { store };
    },

    getNearbyStores: async (): Promise<StoresResponse> => {
        return mockStoreApi.getStores();
    },

    getStoresByCity: async (): Promise<StoresResponse> => {
        return mockStoreApi.getStores();
    },
};

// Mock Shopping List API (uses localStorage)
export const mockShoppingListApi = {
    getShoppingLists: async (): Promise<ShoppingListsResponse> => {
        await mockDelay();
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        
        return {
            lists,
            total: lists.length,
        };
    },

    getShoppingListById: async (listId: string): Promise<ShoppingListResponse> => {
        await mockDelay();
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const list = lists.find(l => l.id === listId);
        
        if (!list) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        return { list };
    },

    createShoppingList: async (data: CreateShoppingListRequest): Promise<ShoppingListResponse> => {
        await mockDelay();
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        
        const newList: SavedShoppingList = {
            id: `list-${Date.now()}`,
            name: data.name,
            items: data.items || {},
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            active: data.active || false,
        };
        
        lists.push(newList);
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        return { list: newList };
    },

    updateShoppingList: async (listId: string, data: UpdateShoppingListRequest): Promise<ShoppingListResponse> => {
        await mockDelay();
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const index = lists.findIndex(l => l.id === listId);
        
        if (index === -1) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        lists[index] = {
            ...lists[index],
            ...data,
            updatedAt: new Date().toISOString(),
        };
        
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        return { list: lists[index] };
    },

    deleteShoppingList: async (listId: string): Promise<void> => {
        await mockDelay();
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const filtered = lists.filter(l => l.id !== listId);
        localStorage.setItem('savedShoppingLists', JSON.stringify(filtered));
    },

    setActiveShoppingList: async (listId: string, active: boolean): Promise<ShoppingListResponse> => {
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        
        // Set all lists to inactive first
        lists.forEach(list => {
            list.active = false;
        });
        
        // Set the target list
        const index = lists.findIndex(l => l.id === listId);
        if (index !== -1) {
            lists[index].active = active;
        }
        
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        if (index === -1) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        return { list: lists[index] };
    },

    addItemsToList: async (listId: string, items: Record<string, number>): Promise<ShoppingListResponse> => {
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const index = lists.findIndex(l => l.id === listId);
        
        if (index === -1) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        lists[index].items = { ...lists[index].items, ...items };
        lists[index].updatedAt = new Date().toISOString();
        
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        return { list: lists[index] };
    },

    removeItemsFromList: async (listId: string, productIds: string[]): Promise<ShoppingListResponse> => {
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const index = lists.findIndex(l => l.id === listId);
        
        if (index === -1) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        productIds.forEach(id => {
            delete lists[index].items[id];
        });
        lists[index].updatedAt = new Date().toISOString();
        
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        return { list: lists[index] };
    },

    updateItemQuantity: async (listId: string, productId: string, quantity: number): Promise<ShoppingListResponse> => {
        const saved = localStorage.getItem('savedShoppingLists');
        const lists: SavedShoppingList[] = saved ? JSON.parse(saved) : [];
        const index = lists.findIndex(l => l.id === listId);
        
        if (index === -1) {
            throw new Error(`Shopping list with ID ${listId} not found`);
        }
        
        if (quantity <= 0) {
            delete lists[index].items[productId];
        } else {
            lists[index].items[productId] = quantity;
        }
        lists[index].updatedAt = new Date().toISOString();
        
        localStorage.setItem('savedShoppingLists', JSON.stringify(lists));
        
        return { list: lists[index] };
    },
};

// Mock AI Route API
export const mockAiRouteApi = {
    optimizeRoute: async (data: OptimizeRouteRequest): Promise<OptimizeRouteResponse> => {
        await mockDelay(2000); // Simulate longer processing time
        
        // Mock route response
        return {
            routeId: `route-${Date.now()}`,
            storeId: data.storeId,
            shoppingListId: data.shoppingListId,
            estimatedTime: 15, // minutes
            totalDistance: 500, // meters
            route: [],
            startNode: {
                id: 0,
                x: 100,
                y: 100,
                products: [],
                type: 'entrance',
            },
            endNode: {
                id: 109,
                x: 500,
                y: 500,
                products: [],
                type: 'checkout',
            },
            createdAt: new Date().toISOString(),
        };
    },

    getRoute: async (_routeId: string): Promise<OptimizeRouteResponse> => {
        await mockDelay();
        throw new Error('Route not found in mock mode');
    },

    getRoutesByShoppingList: async (_shoppingListId: string): Promise<OptimizeRouteResponse[]> => {
        await mockDelay();
        return [];
    },

    deleteRoute: async (_routeId: string): Promise<void> => {
        await mockDelay();
    },
};

// Mock Persona API
export const mockPersonaApi = {
    getPersonas: async (): Promise<Persona[]> => {
        await mockDelay();
        return [
            {
                id: 'Mom',
                name: 'Mom',
                description: 'Mom',
                created_at: '2026-01-26T09:15:47',
                updated_at: null,
            },
            {
                id: 'Student',
                name: 'Student',
                description: 'Student',
                created_at: '2026-01-26T09:15:47',
                updated_at: null,
            },
            {
                id: 'Nana',
                name: 'Nana',
                description: 'Nana',
                created_at: '2026-01-26T09:15:47',
                updated_at: null,
            },
        ];
    },
};
